import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
  Filler, // ⬅️ Add this
} from 'chart.js';
import { FloatingLabel } from 'react-bootstrap';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Title, Filler); // ⬅️ Register it

const colors = [
  '#4facfe', '#43e97b', '#f441a5', '#ffce00',
  '#ff5f6d', '#845ec2', '#ffc75f', '#f9f871',
  '#00c9a7', '#f67280', '#4bc0c0', '#36a2eb'
];

export const SchemeStateLineChart = ({ datasets, selectedScheme, optionsScheme }) => {
  // Build structure: { schemeName: { state: count } }
  const schemeStateMap = {};

  datasets.forEach((dataset) => {
    let schemeName = 'Unknown Scheme';
    let dataItems = [];

    // Handle special cases based on selectedScheme
    if (selectedScheme === "15") {
      // RKVY scheme
      schemeName = optionsScheme?.find(opt => opt.value === selectedScheme)?.label || 'RKVY';
      dataItems = dataset.responseArray || [];
    } else if (selectedScheme === "28") {
      // Patanjali scheme
      schemeName = 'Patanjali';
      dataItems = dataset.responseArray?.features || [];
    } else {
      // Regular schemes
      schemeName = dataset.responseArray?.[0]?.gisSchemeName || 
                   dataset.schemename || 
                   optionsScheme?.find(opt => opt.value === selectedScheme)?.label || 
                   'Unknown Scheme';
      dataItems = dataset.responseArray || [];
    }

    dataItems.forEach((item) => {
      let state = 'Unknown';
      let count = 1;

      // Handle different state field names and count logic
      if (selectedScheme === "15") {
        // RKVY scheme
        state = item?.stateName || item?.farmer_State_Name || 'Unknown';
        count = parseFloat(item.total) || 1;
      } else if (selectedScheme === "28") {
        // Patanjali scheme - extract state from properties
        state = item?.properties?.stateName || item?.properties?.farmer_State_Name || 'Unknown';
        count = 1;
      } else {
        // Regular schemes
        state = item?.stateName || item?.farmer_State_Name || 'Unknown';
        count = 1;
      }

      if (!schemeStateMap[schemeName]) schemeStateMap[schemeName] = {};
      schemeStateMap[schemeName][state] = (schemeStateMap[schemeName][state] || 0) + count;
    });
  });

  // If no data found, show a message
  if (Object.keys(schemeStateMap).length === 0) {
    return (
      <div className='row'>
        <div className='col-md-12' style={{ textAlign: 'center', padding: '50px' }}>
          <h3>No Data Available</h3>
          <p>Please select a scheme and apply filters to view the state-wise chart.</p>
        </div>
      </div>
    );
  }

  const allStates = Array.from(
    new Set(Object.values(schemeStateMap).flatMap(s => Object.keys(s)))
  ).sort();

  const chartDatasets = Object.entries(schemeStateMap).map(([scheme, stateData], index) => ({
    label: scheme,
    data: allStates.map(state => stateData[state] || 0),
    fill: true, // Changed from false to true to make it an area chart
    borderColor: colors[index % colors.length],
    backgroundColor: colors[index % colors.length] + '55', // Optional: Add alpha for transparency
    tension: 0.3,
    pointBackgroundColor: colors[index % colors.length],
    pointBorderColor: '#fff',
    pointRadius: 5,
    borderWidth: 2,
  }));

  const totalSurveys = chartDatasets
    .flatMap(ds => ds.data)
    .reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels: allStates,
    datasets: chartDatasets,
  };

const chartOptions = {
  responsive: true,
  plugins: {
    title: {
      display: true,
      text: 'Survey Count by State ',
      font: { size: 25, weight: 'bold', family: 'Beiruti, sans-serif' }, // ✅ font family added
      color: '#333',
 align: 'start',
      padding: { top: 10, bottom: 20 }
    },
    legend: {
      display: true,
      position: 'bottom',
      labels: {
        font: { size: 14, weight: 'bold', family: 'Beiruti, sans-serif' }, // ✅ already added
        color: '#333'
      }
    },
    tooltip: {
      callbacks: {
        label: (context) =>
          `${context.dataset.label}: ${context.parsed.y.toLocaleString()} surveys`
      }
    }
  },
  scales: {
    x: {
      ticks: {
        font: { weight: 'bold', family: 'Beiruti, sans-serif' }, // ✅ font family added
        color: '#000'
      }
    },
    y: {
      beginAtZero: true,
      ticks: {
        font: { weight: 'bold', family: 'Beiruti, sans-serif' }, // ✅ font family added
        color: '#000',
        callback: (val) => val.toLocaleString()
      }
    }
  }
};


  return (
    <>

  <div className='row'>
  <div className='col-md-8 leftbar'>
    <div  style={{height: '320px' }}>
        <Line data={chartData} options={chartOptions} />
      </div>
  </div>
  <div className='col-md-4'>
<div style={{
        height:'320px',
        maxHeight: '320px',
        display: 'flex',
        flexDirection: 'column',
      
        border: '1px solid #ddd',
        borderRadius: '10px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.05)',
        overflow: 'hidden'
      }}>
        {/* Header */}
         <div style={{ padding: '10px 20px', background: '#f9f9f9', borderBottom: '1px solid #eee' }}>
          <h5 style={{ fontWeight: 700, marginBottom: '8px', color: '#000' }}>
            📍 State-Wise Summary
          </h5>
        </div>

        {/* Scrollable content */}
           <div style={{ overflowY: 'auto', flex: 1, padding: '10px 20px' }}>
          {Object.entries(schemeStateMap).map(([scheme, stateData], schemeIdx) => (
            <div key={scheme} style={{ marginBottom: '14px' }}>
                  <div style={{ fontWeight: 600, marginBottom: '5px', fontSize: '18px', color: '#555' }}>
                🔹 {scheme}
              </div>
              {allStates.map((state, i) => (
                <div key={`${scheme}-${state}`} style={{
                  display: 'flex',
                    alignItems: 'center',
                    fontSize: '14px',
                    marginLeft: '6px',
                    marginBottom: '6px'
                }}>
                  <div
                    style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: colors[schemeIdx % colors.length],
                      borderRadius: '50%',
                      marginRight: '8px'
                    }}
                  />
                  <span style={{ fontWeight: 500 }}>{state}</span>
                  <span style={{ marginLeft: 'auto', color: '#555' }}>
                    {(stateData[state] || 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Sticky total */}
        <div style={{
          padding: '10px 20px',
            background: '#fff',
            borderTop: '1px solid #ddd',
            fontWeight: 600,
            fontSize: '20px',
            textAlign: 'right',
            position: 'sticky',
            color: '#000',
            bottom: 0,
            zIndex: 1
        }}>
          Total: {totalSurveys.toLocaleString()} surveys
        </div>
      </div>

  </div>
  </div>

  
    </>
  );
};
